import { css } from '@emotion/react';
import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import dayjs from 'dayjs';
import { startCase } from 'lodash-es';
import { OfficeUserName } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Accordion, Chip, NakedUl, Text } from '@frontend/design-system';
import { getFullName, getOfficeUser, getStatusChipColor } from '../../utils/call-insight-panel-util';

type CallDetailsSectionProps = {
  call: HydratedCallRecord;
};

export const CallDetailsSection = ({ call }: CallDetailsSectionProps) => {
  const { t } = useTranslation('call-insight-panel');

  const { getScopeName, isSingleTypeScope } = useAppScopeStore();

  const { startedAt, direction, status = '', locationId, callerNumber, dialedNumber, user } = call;

  const statusChipColor = getStatusChipColor(status);

  const isMulti = !isSingleTypeScope;

  return (
    <>
      <Accordion.Header title={t('About This Call')} />
      <Accordion.Body>
        <NakedUl css={styles.list}>
          <li>
            <Text size='medium' color='subdued'>
              {t('Time')}
            </Text>
            <Text size='medium'>{dayjs(startedAt).format('MMM D, YYYY, h:mm A')}</Text>
          </li>
          <li>
            <Text size='medium' color='subdued'>
              {t('Result')}
            </Text>
            {direction === 'outbound' && <span>-</span>}
            {direction === 'inbound' && (
              <Chip isResponsive variant={statusChipColor}>
                {startCase(status)}
              </Chip>
            )}
            {!direction && <span>-</span>}
          </li>
          {isMulti && locationId && (
            <li>
              <Text size='medium' color='subdued'>
                {t('Location')}
              </Text>
              <Chip.Location maxWidth='300'>{getScopeName(locationId)}</Chip.Location>
            </li>
          )}
          <li>
            <Text size='medium' color='subdued'>
              {t('Office User')}
            </Text>
            <OfficeUserName
              displayName={getOfficeUser(call, t('Unknown'))}
              profileName={getFullName(user?.firstName, user?.lastName, t('Unknown'))}
              userId={user?.userId || ''}
            />
          </li>
          <li>
            <Text size='medium' color='subdued'>
              {t('Office Number')}
            </Text>
            <Text size='medium'>
              {direction
                ? formatPhoneNumber(direction === 'outbound' ? callerNumber : dialedNumber)
                : formatPhoneNumber(call.callIntelInsights?.officeNumber?.nationalNumber?.toString())}
            </Text>
          </li>
        </NakedUl>
      </Accordion.Body>
    </>
  );
};

const styles = {
  list: css`
    li {
      align-items: center;
      display: flex;
      justify-content: space-between;
      margin-bottom: ${theme.spacing(2)};

      .data-label {
        color: ${theme.colors.neutral50};
        text-wrap: nowrap;
      }
    }

    li:last-child {
      margin-bottom: ${theme.spacing(0)};
    }
  `,
};
